import { Box, Button, Divider, Group, Image, Modal, Space, Text, Title } from '@mantine/core'
import React, { useEffect, useState } from 'react'
import LcaTableComponent from './LCAReport/LcaTable/LcaTable.component';
import classes from './ReportPdf.module.css';
import DataPieChart from './LCAReport/PieChart/PieChart.component';
import { baseUrl } from '../../Constants/constant';
import mainettilogo from '../../assets/image/mainettilogoimg.png';
import useFetch from '../../Customhooks/useFetch';
import { FaRegArrowAltCircleDown } from 'react-icons/fa';

interface LcaReportProps {
    isConfirmOpen: any;
    closeConfirm: any;
    id: number;
}

interface CustomerData {
    icon_name: string;
}

interface LcaData {
    product_name: string;
    product_description: string;
    report_name: string;
    Customer: CustomerData;
    DatabaseVersion: any;
    ComputeCommentLogo: any;
    created_at: any
    // Add other properties as needed
}

const ReportPdf: React.FC<LcaReportProps> = ({ isConfirmOpen, closeConfirm, id }) => {
    const updatedUrl = baseUrl.replace("/api/", "");
    const token = localStorage.getItem("token");
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [imageUrl, setImageUrl] = useState<string[]>([]);
    const [imageClasses, setImageClasses] = useState<string[]>([]);
    const [pdfGenerationError, setPdfGenerationError] = useState<string | null>(null);

    const { data: datalist, error: getlistError, fetchData: gettable } = useFetch({
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            token: token,
        },
    });

    const {
        data: Base64Data,
        error: resError,
        fetchData: fetchBaseString
    } = useFetch({
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            token: token,
        },
        body: JSON.stringify({
            images: imageUrl
        })
    });

    useEffect(() => {
        if (id) {
            gettable(`${baseUrl}compute/lca/calculateLca/${id}`);
        }
    }, [id]);

    useEffect(() => {
        const customerIcon = datalist?.data?.data[0]?.ComputeCommentLogo?.ComputeCustomerLogo?.icon_name;
        const verticalIcon = datalist?.data?.data[0]?.product_image;

        const images: any = [
            customerIcon ? `${updatedUrl}${customerIcon}` : null,
            verticalIcon ? `${updatedUrl}${verticalIcon}` : null
        ].filter(Boolean); // removes nulls

        if (images.length > 0) {
            setImageUrl(images);
        }
    }, [datalist, updatedUrl]);

    useEffect(() => {
        if (imageUrl.length > 0) {
            fetchBaseString(`${baseUrl}compute/lca/loadImages`);
        }
    }, [imageUrl]);

    const data: LcaData | undefined = datalist?.data?.data[0];

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>, index: number) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    const ratio = naturalWidth / naturalHeight;

    let styleClass = 'ratioOther';
    if (Math.abs(ratio - 1) < 0.05) {
      styleClass = 'ratioSquare';
    } else if (Math.abs(ratio - 2) < 0.05) {
      styleClass = 'ratioWide';
    }

    setImageClasses((prev) => {
      const updated = [...prev];
      updated[index] = styleClass;
      return updated;
    });
  };

    const handleDownloadPDF = async () => {
        setIsGeneratingPDF(true);
        setPdfGenerationError(null);

        try {
            // Lazy import html2pdf
            const html2pdf = (await import('html2pdf.js')).default;

            const element = document.getElementById('pdf-report-container');

            if (!element) {
                throw new Error("PDF element not found");
            }

            const opt = {
                margin: 2,
                filename: `${data?.product_name || 'LCA'}_report.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    allowTaint: true,
                    foreignObjectRendering: false
                },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };

            // Give the browser time to properly render the content before generating PDF
            await new Promise(resolve => setTimeout(resolve, 500));

            await html2pdf().from(element).set(opt).save();
        } catch (error) {
            console.error("Error generating PDF:", error);
            setPdfGenerationError("Failed to generate PDF. Please try again.");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    if (getlistError) {
        return <Box><Text color="red">Error loading LCA data. Please try again later.</Text></Box>;
    }
    return (
        <Modal opened={isConfirmOpen} onClose={closeConfirm} size="xl" centered title={"Preview Report"}>
            <>
                {pdfGenerationError && <Text color="red" mt="sm">{pdfGenerationError}</Text>}
                <Space h="lg" />
                <Divider />

                {data ? (
                    <Box id="pdf-report-container" className={classes.pdf_wrapper}>
                        <Box className={classes.report_container}>
                            <Box className={classes.report_header}>
                                <Image
                                    radius="md"
                                    h={'auto'}
                                    w={'70%'}
                                    pl={16}
                                    fit="contain"
                                    src={mainettilogo}
                                    alt="Mainetti Logo"
                                />
                                <div className={classes.report_left_header}>

                                    <Text size='xl' fw={700}>PRODUCT LIFECYCLE ASSESSMENT</Text>
                                    <Space h="xs" />
                                    <Box>
                                        <Title order={4} tt="uppercase">REPORT NAME: {data.report_name}</Title>
                                        <Title order={4} tt="uppercase">PRODUCT NAME: {data.product_name}</Title>
                                        <Title order={4} tt="uppercase">DATABASE VERSION: {data?.DatabaseVersion?.db_version}</Title>
                                        <Box w={350} mt={24}>
                                            <Text size="xl" lh={1} component="div"
                                                style={{
                                                    wordBreak: 'break-all',
                                                    whiteSpace: 'normal',
                                                }}
                                            >
                                                <Title
                                                    order={4}
                                                    style={{ display: 'inline', textTransform: 'uppercase', marginRight: 4 }}
                                                >
                                                    DESCRIPTION :
                                                </Title>{data.product_description}
                                            </Text>
                                        </Box>
                                    </Box>
                                </div>
                            </Box>
                            <Box className={classes.image_container}>
                                {[0, 1].map((index:number) => (
                                    <Box className={classes.customer_image} key={index}>
                                        {Base64Data?.data?.[index] && (
                                            <Image
                                                fit="contain"
                                                src={Base64Data.data[index]}
                                                alt={`Customer logo ${index + 1}`}
                                                onLoad={(e) => handleImageLoad(e, index)}
                                                className={classes[imageClasses[index]]}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Space h={'xl'} />
                        <Box style={{ display: 'flex' }}>
                            <Box className={classes.table_comp}>
                                <LcaTableComponent data={data} />
                            </Box>
                            <Box className={classes.pie_comp}><DataPieChart data={data} /></Box>
                        </Box>
                        <Space h={'lg'} />
                        {data?.ComputeCommentLogo?.report_comment && (<Box style={{
                            padding: '4px',
                            margin: '0 25px 0 25px'
                        }}>
                            <Text
                                fw="400"
                                fs="20px"
                                style={{
                                    wordBreak: 'break-all',
                                    whiteSpace: 'normal',
                                }}

                            >{data?.ComputeCommentLogo?.report_comment}
                            </Text>
                        </Box>)}
                        <Box className={classes.pdf_footer}>
                            <Text component="span" size="lg">
                                {data?.created_at?.split('T')[0]}
                            </Text>
                            <Text component="span" size="lg">
                                ©MAINETTI
                            </Text>
                        </Box>
                    </Box>
                ) : (
                    <Box py="xl" ta="center">
                        <Text>Loading report data...</Text>
                    </Box>
                )}
            </>
            <Group mt="sm" justify="center">
                <Button
                    variant="gradient"
                    w={125}
                    gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                    radius={6}
                    size='sm'
                    leftSection={<FaRegArrowAltCircleDown size={18} />}
                    onClick={handleDownloadPDF}
                    loading={isGeneratingPDF}
                    disabled={isGeneratingPDF || !data}
                >
                    Download
                </Button>
            </Group>
        </Modal>
    )
}

export default ReportPdf